// DataRequestsPanel.jsx
// Settings → Data requests. The system administrator's queue of "forget me" requests,
// worked to closure (0033).
//
// Shown only to SYSTEM_ADMIN_EMAILS (ProfileSettingsPage asks /api/me/data-request).
// That is a courtesy; requireSystemAdmin on /api/admin/data-requests/* is the control.
//
// Closing requires a note — what was deleted, and anything retained under a legal
// obligation and why. The Privacy Policy promises the user that account, and the note
// is shown back to them in their own Privacy section.

import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import './UserApprovalPanel.css';
import './DataRequests.css';

const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', closed: 'Closed' };

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

function daysLeft(dueIso) {
  return Math.ceil((new Date(dueIso).getTime() - Date.now()) / 86400000);
}

export default function DataRequestsPanel({ onCountChange }) {
  const [requests, setRequests] = useState(null);
  const [showAll, setShowAll]   = useState(false);
  const [error, setError]       = useState('');
  const [busyId, setBusyId]     = useState(null);
  const [notes, setNotes]       = useState({});

  const load = useCallback(async () => {
    setError('');
    try {
      const res  = await apiFetch(`/api/admin/data-requests/list${showAll ? '?status=all' : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setRequests(data.requests || []);
      if (onCountChange) onCountChange(data.openCount || 0);
    } catch (e) {
      setError(e.message);
      setRequests([]);
    }
  }, [showAll, onCountChange]);

  useEffect(() => { load(); }, [load]);

  async function setStatus(r, status) {
    setBusyId(r.id);
    setError('');
    try {
      const adminNote = notes[r.id] !== undefined ? notes[r.id] : (r.admin_note || '');
      const res  = await apiFetch('/api/admin/data-requests/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: r.id, status, adminNote }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Update failed (${res.status})`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  if (requests === null) return <div className="uap-empty">Loading…</div>;

  return (
    <div className="uap">
      <h3 className="uap-title">Data deletion requests</h3>
      <p className="uap-lede">
        Each request must be closed within 30 days of filing, including backups and
        archives. Deletion is done by hand; this queue is the record. When closing, note
        what was deleted and anything retained for legal or regulatory reasons — the
        requester sees that note.
      </p>

      <label className="dr-toggle">
        <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
        Show closed requests
      </label>

      {error && <div className="uap-error" role="alert">{error}</div>}

      {requests.length === 0 ? (
        <div className="uap-empty">
          {showAll ? 'No requests yet.' : 'No open requests.'}
          <button type="button" className="uap-link" onClick={load}>Refresh</button>
        </div>
      ) : (
        <ul className="uap-list">
          {requests.map(r => {
            const open = r.status !== 'closed';
            const left = daysLeft(r.due_at);
            const noteValue = notes[r.id] !== undefined ? notes[r.id] : (r.admin_note || '');
            return (
              <li key={r.id} className={`uap-row dr-row${open && left < 0 ? ' dr-row--overdue' : ''}`}>
                <div className="uap-who">
                  <span className="uap-email">{r.requester_email}</span>
                  {r.requester_name && <span className="uap-name">{r.requester_name}</span>}
                  <span className="uap-when">
                    {STATUS_LABEL[r.status] || r.status} · filed {fmtDate(r.created_at)} ·{' '}
                    {open
                      ? (left < 0 ? <strong className="dr-overdue">overdue by {-left} day{left === -1 ? '' : 's'}</strong>
                                  : `due ${fmtDate(r.due_at)} (${left} day${left === 1 ? '' : 's'} left)`)
                      : `closed ${fmtDate(r.closed_at)} by ${r.closed_by || '—'}`}
                  </span>
                  <span className="dr-ids">user {r.user_id}{r.tenant_id ? ` · tenant ${r.tenant_id}` : ' · no tenant (pending)'}</span>
                  {r.user_note && <div className="dr-note">“{r.user_note}”</div>}
                </div>

                <div className="dr-work">
                  <textarea
                    className="dr-textarea"
                    placeholder="What was deleted, and anything retained and why"
                    value={noteValue}
                    onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
                    rows={2}
                    disabled={busyId === r.id}
                  />
                  <div className="uap-actions">
                    {r.status === 'open' && (
                      <button type="button" className="uap-btn uap-btn--ghost" disabled={busyId === r.id}
                              onClick={() => setStatus(r, 'in_progress')}>Mark in progress</button>
                    )}
                    {open ? (
                      <button type="button" className="uap-btn uap-btn--primary"
                              disabled={busyId === r.id || !noteValue.trim()}
                              onClick={() => setStatus(r, 'closed')}>Close as completed</button>
                    ) : (
                      <button type="button" className="uap-btn uap-btn--ghost" disabled={busyId === r.id}
                              onClick={() => setStatus(r, 'open')}>Reopen</button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
